/**
 * AM Inbox — Conversations page for Account Managers
 *
 * Shows a list of client conversations on the left and the active
 * chat thread on the right. Supports real-time updates.
 */

import { useState, useEffect } from "react";
import { AMLayout } from "@/components/am/AMLayout";
import { useAccountManager } from "@/hooks/useAccountManager";
import { useAMConversations } from "@/hooks/useAMMessages";
import ClientChat from "@/components/am/ClientChat";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Search,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatRelativeTime(ts: string): string {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export default function AMInbox() {
  const isMobile = useIsMobile();
  const { accountManager, loading: amLoading } = useAccountManager();
  const { conversations, loading: convsLoading } = useAMConversations(
    accountManager?.id
  );

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userId, setUserId] = useState("");

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setUserId(data.user.id);
    });
  }, []);

  const filteredConversations = searchQuery
    ? conversations.filter(
        (c) =>
          c.agent_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (c.agent_company || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
      )
    : conversations;

  const selectedConversation = conversations.find(
    (c) => c.agent_id === selectedAgentId
  );

  const totalUnread = conversations.reduce(
    (sum, c) => sum + c.unread_count,
    0
  );

  const selectConversation = (agentId: string) => {
    setSelectedAgentId(agentId);
    setMobileShowChat(true);
  };

  if (amLoading || convsLoading) {
    return (
      <AMLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </AMLayout>
    );
  }

  return (
    <AMLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          Inbox
          {totalUnread > 0 && (
            <Badge className="ml-2 bg-primary text-primary-foreground">
              {totalUnread}
            </Badge>
          )}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Client conversations
        </p>
      </div>

      <div
        className="flex gap-0 sm:gap-4 min-h-0"
        style={{ height: "calc(100vh - 16rem)" }}
      >
        {/* Conversation List */}
        <div
          className={`${
            isMobile ? "w-full" : "w-80"
          } bg-card rounded-2xl border border-border flex flex-col flex-shrink-0 ${
            isMobile && mobileShowChat ? "hidden" : ""
          }`}
        >
          {/* Search */}
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9 text-sm"
              />
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="p-6 text-center">
                <MessageSquare className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">
                  {conversations.length === 0
                    ? "No clients assigned yet."
                    : "No clients match your search."}
                </p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <button
                  key={conv.agent_id}
                  onClick={() => selectConversation(conv.agent_id)}
                  className={`w-full text-left p-4 border-b border-border transition-colors ${
                    selectedAgentId === conv.agent_id
                      ? "bg-[#D4A853]/10 border-l-2 border-l-[#D4A853]"
                      : "hover:bg-muted/50 border-l-2 border-l-transparent"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground font-semibold text-sm flex-shrink-0">
                      {getInitials(conv.agent_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-foreground text-sm truncate">
                          {conv.agent_name}
                        </p>
                        {conv.last_message_at && (
                          <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">
                            {formatRelativeTime(conv.last_message_at)}
                          </span>
                        )}
                      </div>
                      {conv.agent_company && (
                        <p className="text-[10px] text-muted-foreground">
                          {conv.agent_company}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {conv.last_message || "No messages yet"}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-[10px] font-bold flex-shrink-0">
                        {conv.unread_count}
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="p-3 border-t border-border">
            <p className="text-[10px] text-muted-foreground text-center">
              {conversations.length} client
              {conversations.length !== 1 ? "s" : ""}
              {totalUnread > 0 && ` · ${totalUnread} unread`}
            </p>
          </div>
        </div>

        {/* Chat Panel */}
        <div
          className={`flex-1 bg-card rounded-2xl border border-border flex flex-col min-h-0 ${
            isMobile && !mobileShowChat ? "hidden" : ""
          }`}
        >
          {isMobile && mobileShowChat && (
            <div className="p-2 border-b border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileShowChat(false)}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            </div>
          )}

          {selectedConversation && accountManager ? (
            <ClientChat
              conversation={selectedConversation}
              amId={accountManager.id}
              senderId={userId}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Select a client to start messaging
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AMLayout>
  );
}
